import { Box } from "@mui/material";
import React, { useState } from "react";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import ClientInfoModal from "./components/ClientInfoModal";
import DatePicker from "./components/DatePicker";
import LocationPicker from "./components/LocationPicker";
import PersonalisationModal from "./components/PersonalisationModal";
import PersonaliseChoiceModal from "./components/PersonaliseChoiceModal";
import ThankYouModal from "./components/ThankYouModalToast";
import TimePicker from "./components/TimePicker";
import QuoteInputCard from "./components/QuoteInputCard";
import QuotePreview from "./components/QoutePreview";

interface Deceased {
  salutation?: string;
  first_name: string;
  second_name?: string;
  surname: string;
  nickname?: string;
  gender?: string;
  date_of_birth?: string;
  date_of_death?: string;
  relationship_to_deceased?: string;
  religion?: string;
  denomination?: string;
  orbituary?: string;
  source_of_record?: string;
  person_image?: File;
}

interface Contact {
  first_name: string;
  last_name?: string;
  phone: string;
}

interface QuotePayload {
  deceased: Deceased;
  contact: Contact;
  deceased_id?: number | null;
  event_datetime: string;
  venue_name: string;
  venue_latitude: string;
  venue_longitude: string;
}

interface QuoteResponse {
  id: number;
  reference_number: string;
  deceased: Deceased;
  contact: Contact;
  deceased_id?: number | null;
  event_datetime: string;
  venue_name: string;
  venue_latitude: string;
  venue_longitude: string;
}

const buildQuoteFormData = (payload: QuotePayload, photo?: File | null) => {
  const formData = new FormData();

  formData.append("deceased.first_name", payload.deceased.first_name);
  formData.append("deceased.surname", payload.deceased.surname);

  if (photo) {
    formData.append("deceased.person_image", photo);
  }

  if (payload.deceased.date_of_birth)
    formData.append("deceased.date_of_birth", payload.deceased.date_of_birth);
  if (payload.deceased.date_of_death)
    formData.append("deceased.date_of_death", payload.deceased.date_of_death);

  formData.append("contact.first_name", payload.contact.first_name);
  if (payload.contact.last_name) {
    formData.append("contact.last_name", payload.contact.last_name);
  }
  formData.append("contact.phone", payload.contact.phone);

  formData.append("event_datetime", payload.event_datetime);
  formData.append("venue_name", payload.venue_name);
  formData.append("venue_latitude", payload.venue_latitude);
  formData.append("venue_longitude", payload.venue_longitude);

  return formData;
};

interface ServiceQuoteFormProps {
  buttonText?: string;
  buttonColor?: string;
}

const ServiceQuoteForm: React.FC<ServiceQuoteFormProps> = ({ 
  buttonText = "Get Estimate", 
  buttonColor = "#614338"
}) => {
  const [formData, setFormData] = useState({
    when: "",
    where: "",
    whatTime: "",
    whatsappNumber: "",
    fullName: "",
    belovedName: "",
    churchName: "",
    burialLocation: "",
    photo: null as File | null,
  });

  const [currentModal, setCurrentModal] = useState<
    "whatsapp" | "Personalise" | "details" | null
  >(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const [whenAnchor, setWhenAnchor] = useState<HTMLElement | null>(null);
  const [whereAnchor, setWhereAnchor] = useState<HTMLElement | null>(null);
  const [timeAnchor, setTimeAnchor] = useState<HTMLElement | null>(null);

  const [showThankYou, setShowThankYou] = useState(false);
  const [createdQuoteId, setCreatedQuoteId] = useState<number | null>(null);
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [belovedName, setBelovedName] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const splitFullName = (fullName: string) => {
    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 0) {
      return { first_name: "", last_name: "" };
    }

    if (parts.length === 1) {
      return { first_name: parts[0], last_name: "" };
    }

    return {
      first_name: parts.slice(0, -1).join(" "),
      last_name: parts[parts.length - 1],
    };
  };

  const normalizeName = (name: string) =>
    name
      .split(" ")
      .map((n) => n.charAt(0).toUpperCase() + n.slice(1).toLowerCase())
      .join(" ");

  // Reset form
  const resetForm = () => {
    setFormData({
      when: "",
      where: "",
      whatTime: "",
      whatsappNumber: "",
      fullName: "",
      belovedName: "",
      churchName: "",
      burialLocation: "",
      photo: null,
    });
    setPhotoPreview(null);
    setCurrentModal(null);
    setWhenAnchor(null);
    setWhereAnchor(null);
    setTimeAnchor(null);
  };

  // Step handlers
  const handleGetQuote = () => {
    if (!formData.when || !formData.where || !formData.whatTime) {
      toast.error("Please fill in all event details.");
      return;
    }
    setCurrentModal("whatsapp");
  };

  const handleWhatsAppSubmit = () => {
    if (!formData.fullName.trim().includes(" ")) {
      toast.error("Please enter your full name.");
      return;
    }

    if (!formData.whatsappNumber.trim()) {
      toast.error("Please enter a valid WhatsApp number.");
      return;
    }
    setCurrentModal("Personalise");
  };

  const submitMinimalQuote = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const eventDatetime = `${formData.when}T${formData.whatTime}:00`;
    const { first_name, last_name } = splitFullName(formData.fullName);

    const payload: QuotePayload = {
      deceased: {
        first_name: "Not",
        surname: "provided",
      },
      contact: {
        first_name: normalizeName(first_name),
        last_name: normalizeName(last_name),
        phone: formData.whatsappNumber,
      },
      event_datetime: eventDatetime,
      venue_name: formData.where,
      venue_latitude: "0.0",
      venue_longitude: "0.0",
    };

    try {
      const multipartData = buildQuoteFormData(payload, null);

      const response = await apiService.post<QuoteResponse>(
        "/api/bookings/quote-request/",
        multipartData,
      );

      setCreatedQuoteId(response.data.id);
      setReferenceNumber(response.data.reference_number);
      setBelovedName(
        `${response.data.deceased.first_name} ${response.data.deceased.surname}`,
      );

      setShowThankYou(true);
      resetForm();
    } catch (err: unknown) {
      console.error("Failed to submit quote request", err);
      toast.error("Failed to submit quote request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePersonaliseNo = () => {
    submitMinimalQuote();
  };

  const handlePersonaliseYes = () => {
    setCurrentModal("details");
  };

  const handleDetailsSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const eventDatetime = `${formData.when}T${formData.whatTime}:00`;
    const { first_name, last_name } = splitFullName(formData.belovedName);
    const { first_name: contactFirst, last_name: contactLast } = splitFullName(
      formData.fullName,
    );

    const payload: QuotePayload = {
      deceased: {
        first_name: normalizeName(first_name),
        surname: normalizeName(last_name || "Unknown"),
      },
      contact: {
        first_name: normalizeName(contactFirst),
        last_name: normalizeName(contactLast),
        phone: formData.whatsappNumber,
      },
      event_datetime: eventDatetime,
      venue_name: formData.where,
      venue_latitude: "0.0",
      venue_longitude: "0.0",
    };

    try {
      const multipartData = buildQuoteFormData(payload, formData.photo);

      const response = await apiService.post<QuoteResponse>(
        "/api/bookings/quote-request/",
        multipartData,
      );

      setCreatedQuoteId(response.data.id);
      setReferenceNumber(response.data.reference_number);
      setBelovedName(
        `${response.data.deceased.first_name} ${response.data.deceased.surname}`,
      );

      setShowThankYou(true);
      resetForm();
    } catch (err: unknown) {
      console.error("Failed to submit quote request", err);
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipDetails = async () => {
    submitMinimalQuote();
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Photo must be under 10MB.");
        return;
      }
      setFormData({ ...formData, photo: file });
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const closeModal = () => setCurrentModal(null);

  const formatDate = (date: string) => {
    if (!date) return "Add dates";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    if (!time) return "Add time";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const whereBoxRef = React.useRef<HTMLDivElement>(null);
  const timeBoxRef = React.useRef<HTMLDivElement>(null);

  const previewServiceDate = formData.when
    ? formatDate(formData.when)
    : undefined;

  const previewServiceTime = formData.whatTime
    ? formatTime(formData.whatTime)
    : undefined;

  const previewLocation = formData.where || undefined;

  const showPreview = Boolean(
    formData.when || formData.whatTime || formData.where,
  );

  return (
    <Box>
      {/* Main Form Card */}
      <QuoteInputCard
        when={formData.when}
        where={formData.where}
        whatTime={formData.whatTime}
        whereBoxRef={whereBoxRef}
        timeBoxRef={timeBoxRef}
        onWhenClick={(el) => setWhenAnchor(el)}
        onWhereClick={(el) => setWhereAnchor(el)}
        onTimeClick={(el) => setTimeAnchor(el)}
        onGetQuote={handleGetQuote}
        formatDate={formatDate}
        formatTime={formatTime}
        buttonText={buttonText}
        buttonColor={buttonColor}
      />

      {/* Preview */}
      <Box
        sx={{
          minHeight: showPreview ? 320 : 0,
          position: "relative",
          display: { xs: "none", md: showPreview ? "block" : "none" },
        }}
      >
        {showPreview && (
          <Box
            sx={{
              position: "absolute",
              left: "100%",
              marginLeft: 1,
              top: -115,
              width: 360,
              animation: "slideUp 0.35s ease",
              "@keyframes slideUp": {
                from: { opacity: 0, transform: "translateY(20px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <QuotePreview
              belovedName={formData.belovedName || "Beloved Name"}
              eventDate={previewServiceDate}
              eventTime={previewServiceTime}
              location={previewLocation}
              clientName={formData.fullName}
              clientPhone={formData.whatsappNumber}
              photoPreview={photoPreview}
            />
          </Box>
        )}
      </Box>

      {/* Custom Pickers */}
      <DatePicker
        open={Boolean(whenAnchor)}
        anchorEl={whenAnchor}
        onClose={() => setWhenAnchor(null)}
        value={formData.when}
        onChange={(date) => {
          setFormData({ ...formData, when: date });
          setWhenAnchor(null);
          if (whereBoxRef.current) {
            setWhereAnchor(whereBoxRef.current);
          }
        }}
        disablePast={true}
        showFlexibleDates={true}
      />

      <LocationPicker
        open={Boolean(whereAnchor)}
        anchorEl={whereAnchor}
        onClose={() => setWhereAnchor(null)}
        value={formData.where}
        onChange={(location) => {
          setFormData({ ...formData, where: location });
          setWhereAnchor(null);
          if (timeBoxRef.current) {
            setTimeAnchor(timeBoxRef.current);
          }
        }}
      />

      <TimePicker
        open={Boolean(timeAnchor)}
        anchorEl={timeAnchor}
        onClose={() => setTimeAnchor(null)}
        value={formData.whatTime}
        onChange={(time) => {
          setFormData({ ...formData, whatTime: time });
          setTimeAnchor(null);
        }}
        format="12h"
      />

      {/* Modals */}
      <ClientInfoModal
        open={currentModal === "whatsapp"}
        onClose={closeModal}
        whatsappNumber={formData.whatsappNumber}
        onNumberChange={(val) =>
          setFormData((prev) => ({ ...prev, whatsappNumber: val }))
        }
        fullName={formData.fullName}
        onFullNameChange={(val) =>
          setFormData((prev) => ({ ...prev, fullName: val }))
        }
        onSubmit={handleWhatsAppSubmit}
      />

      <PersonaliseChoiceModal
        open={currentModal === "Personalise"}
        onClose={closeModal}
        onYes={handlePersonaliseYes}
        onNo={handlePersonaliseNo}
        isSubmitting={isSubmitting}
      />

      <PersonalisationModal
        open={currentModal === "details"}
        onClose={closeModal}
        where={formData.where}
        belovedName={formData.belovedName}
        churchName={formData.churchName}
        burialLocation={formData.burialLocation}
        onBelovedNameChange={(val) =>
          setFormData((prev) => ({ ...prev, belovedName: val }))
        }
        onChurchNameChange={(val) =>
          setFormData((prev) => ({ ...prev, churchName: val }))
        }
        onBurialLocationChange={(val) =>
          setFormData((prev) => ({ ...prev, burialLocation: val }))
        }
        onPhotoUpload={handlePhotoUpload}
        photoPreview={photoPreview}
        onSubmit={handleDetailsSubmit}
        onSkip={handleSkipDetails}
        isSubmitting={isSubmitting}
      />
      <ThankYouModal
        open={showThankYou}
        onClose={() => {
          setShowThankYou(false);
          setCurrentModal(null);
          setCreatedQuoteId(null);
          setReferenceNumber("");
        }}
        quoteId={createdQuoteId}
        belovedName={belovedName}
        referenceNumber={referenceNumber}
      />
    </Box>
  );
};

export default ServiceQuoteForm;
